import { z } from 'zod';

// ============================================
// LLM 调用基础类型
// ============================================

export const LLMMessageRoleSchema = z.enum(['system', 'user', 'assistant']);
export type LLMMessageRole = z.infer<typeof LLMMessageRoleSchema>;

export const LLMMessageSchema = z.object({
  role: LLMMessageRoleSchema,
  content: z.string().default(''),
});
export type LLMMessage = z.infer<typeof LLMMessageSchema>;

export const CustomPromptPostProcessingTypeSchema = z.enum(['', 'merge', 'semi', 'strict']);
export type CustomPromptPostProcessingType = z.infer<typeof CustomPromptPostProcessingTypeSchema>;

export const STRegexPlacementNumberSchema = z.union([
  z.literal(1), // USER_INPUT
  z.literal(2), // AI_OUTPUT
  z.literal(3), // SLASH_COMMAND
  z.literal(5), // WORLD_INFO
  z.literal(6), // REASONING
]);
export type STRegexPlacementNumber = z.infer<typeof STRegexPlacementNumberSchema>;

export const STSubstituteRegexModeSchema = z.union([z.literal(0), z.literal(1), z.literal(2)]);
export type STSubstituteRegexMode = z.infer<typeof STSubstituteRegexModeSchema>;

export const RegexRuleSchema = z.object({
  id: z.string().min(1),
  // ===== SillyTavern 原生字段 =====
  scriptName: z.string().default('新规则'),
  findRegex: z.string().default(''),
  replaceString: z.string().default(''),
  trimStrings: z.array(z.string()).default([]),
  placement: z.array(STRegexPlacementNumberSchema).default([2]),
  disabled: z.boolean().default(false),
  markdownOnly: z.boolean().default(false),
  promptOnly: z.boolean().default(false),
  runOnEdit: z.boolean().default(true),
  substituteRegex: STSubstituteRegexModeSchema.default(0),
  minDepth: z.number().int().nullable().default(null),
  maxDepth: z.number().int().nullable().default(null),
  // 非酒馆字段：用于当前项目的稳定顺序
  order: z.number().int().default(100),
  // 非酒馆字段：拆分/导入时的来源标记，便于后续排查
  sourceMeta: z
    .object({
      importedFrom: z.string().optional(),
    })
    .optional(),
});
export type RegexRule = z.infer<typeof RegexRuleSchema>;

export const RegexConfigSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).default('默认正则配置'),
  enabled: z.boolean().default(true),
  globalRules: z.array(RegexRuleSchema).default([]),
  presetRules: z.array(RegexRuleSchema).default([]),
});
export type RegexConfig = z.infer<typeof RegexConfigSchema>;

export const WorldBookInjectionModeSchema = z.enum([
  'relative_before',
  'relative_after',
  'before_example',
  'after_example',
  'before_author_note',
  'after_author_note',
  'in_chat',
  'depth',
  'outlet',
]);
export type WorldBookInjectionMode = z.infer<typeof WorldBookInjectionModeSchema>;

export const WorldBookMatchLogicSchema = z.enum(['any', 'all']);
export type WorldBookMatchLogic = z.infer<typeof WorldBookMatchLogicSchema>;

export const WorldBookGlobalSettingsSchema = z.object({
  scan_depth: z.number().int().min(0).nullable().default(2),
  recursive_scan: z.boolean().default(true),
  max_recursion_rounds: z.number().int().min(0).nullable().default(null),
});
export type WorldBookGlobalSettings = z.infer<typeof WorldBookGlobalSettingsSchema>;

export const STPromptNodeSchema = z.object({
  identifier: z.string().min(1),
  name: z.string().optional(),
  role: LLMMessageRoleSchema.default('system'),
  content: z.string().default(''),
  system_prompt: z.boolean().optional(),
  injection_position: z.number().int().optional(),
  injection_depth: z.number().int().optional(),
  injection_order: z.number().int().optional(),
  injection_trigger: z.array(z.string()).optional(),
  forbid_overrides: z.boolean().optional(),
  extension: z.boolean().optional(),
  marker: z.boolean().optional(),
  enabled: z.boolean().optional(),
});
export type STPromptNode = z.infer<typeof STPromptNodeSchema>;

export const STPromptOrderEntrySchema = z.object({
  identifier: z.string().min(1),
  enabled: z.boolean().default(true),
});
export type STPromptOrderEntry = z.infer<typeof STPromptOrderEntrySchema>;

export const STPromptOrderBlockSchema = z.object({
  character_id: z.number().int().default(100001),
  order: z.array(STPromptOrderEntrySchema).default([]),
});
export type STPromptOrderBlock = z.infer<typeof STPromptOrderBlockSchema>;

export const STPresetTemplateKindSchema = z.enum(['garden_builtin', 'compatibility']);
export type STPresetTemplateKind = z.infer<typeof STPresetTemplateKindSchema>;

export const DEFAULT_ST_OPENAI_TEMPERATURE = 1;
export const DEFAULT_ST_OPENAI_TOP_P = 1;
export const DEFAULT_ST_OPENAI_TOP_K = 0;
export const DEFAULT_ST_OPENAI_TOP_A = 0;
export const DEFAULT_ST_OPENAI_MIN_P = 0;
export const DEFAULT_ST_OPENAI_REPETITION_PENALTY = 1;
export const DEFAULT_ST_OPENAI_MAX_TOKENS = 20000;
export const DEFAULT_ST_OPENAI_PRESENCE_PENALTY = 0;
export const DEFAULT_ST_OPENAI_FREQUENCY_PENALTY = 0;
export const DEFAULT_CONVERSATION_PROMPT_TOKEN_BUDGET = 6000;
export const DEFAULT_MEMORY_SUMMARY_QUALITY_MAX_RETRIES = 1;
export const DEFAULT_MEMORY_SESSION_SUMMARY_MIN_LENGTH = 24;
export const DEFAULT_MEMORY_STAGE_SUMMARY_MIN_LENGTH = 36;
export const DEFAULT_MEMORY_VECTOR_RECALL_LIMIT = 4;
export const DEFAULT_MEMORY_VECTOR_RECALL_CONTENT_LIMIT = 220;

export const LLMTemperatureSchema = z.number().min(0).max(2).nullable().default(DEFAULT_ST_OPENAI_TEMPERATURE);
export const LLMTopPSchema = z.number().min(0).max(1).nullable().default(DEFAULT_ST_OPENAI_TOP_P);
export const LLMTopKSchema = z.number().int().min(0).max(500).nullable().default(DEFAULT_ST_OPENAI_TOP_K);
export const LLMTopASchema = z.number().min(0).max(1).nullable().default(DEFAULT_ST_OPENAI_TOP_A);
export const LLMMinPSchema = z.number().min(0).max(1).nullable().default(DEFAULT_ST_OPENAI_MIN_P);
export const LLMRepetitionPenaltySchema = z
  .number()
  .min(1)
  .max(2)
  .nullable()
  .default(DEFAULT_ST_OPENAI_REPETITION_PENALTY);
export const LLMPenaltySchema = z.number().min(-2).max(2).nullable().default(DEFAULT_ST_OPENAI_PRESENCE_PENALTY);
export const LLMMaxTokensSchema = z.number().int().min(1).nullable().default(DEFAULT_ST_OPENAI_MAX_TOKENS);
export const ConversationPromptTokenBudgetSchema = z
  .number()
  .int()
  .min(1)
  .nullable()
  .default(DEFAULT_CONVERSATION_PROMPT_TOKEN_BUDGET);

export const MemorySettingsSchema = z.object({
  conversationPromptTokenBudget: ConversationPromptTokenBudgetSchema,
  summaryQualityMaxRetries: z.number().int().min(0).max(3).default(DEFAULT_MEMORY_SUMMARY_QUALITY_MAX_RETRIES),
  sessionSummaryMinLength: z.number().int().min(1).default(DEFAULT_MEMORY_SESSION_SUMMARY_MIN_LENGTH),
  stageSummaryMinLength: z.number().int().min(1).default(DEFAULT_MEMORY_STAGE_SUMMARY_MIN_LENGTH),
  vectorRecallLimit: z.number().int().min(0).max(20).default(DEFAULT_MEMORY_VECTOR_RECALL_LIMIT),
  vectorRecallContentLimit: z.number().int().min(40).max(2000).default(DEFAULT_MEMORY_VECTOR_RECALL_CONTENT_LIMIT),
});
export type MemorySettings = z.infer<typeof MemorySettingsSchema>;

// ============================================
// LLM 配置基础类型
// ============================================

export const LLMProviderTypeSchema = z.enum(['openai-compatible']);
export type LLMProviderType = z.infer<typeof LLMProviderTypeSchema>;

export const ApiConfigSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).default('默认 API 配置'),
  provider: LLMProviderTypeSchema.default('openai-compatible'),
  baseUrl: z.string().min(1).default('https://api.openai.com/v1'),
  model: z.string().min(1).default('gpt-4.1-mini'),
  apiKey: z.string().default(''),
  lightweightBaseUrl: z.string().default(''),
  lightweightModel: z.string().default(''),
  lightweightApiKey: z.string().default(''),
  embeddingEnabled: z.boolean().default(true),
  embeddingBaseUrl: z.string().default(''),
  embeddingModel: z.string().default('text-embedding-3-small'),
  embeddingApiKey: z.string().default(''),
  temperature: LLMTemperatureSchema,
  topP: LLMTopPSchema,
  maxTokens: LLMMaxTokensSchema,
  presencePenalty: LLMPenaltySchema,
  frequencyPenalty: LLMPenaltySchema,
  sendExtendedSamplingParams: z.boolean().default(false),
  narrativeStream: z.boolean().default(false),
});
export type ApiConfig = z.infer<typeof ApiConfigSchema>;
